// Curated "What's New" release history shown in ReleaseHistoryModal.
//
// Newest-first. Prepend an entry here for every user-visible minor release
// (a new /VERSION minor bump). `version` is the base minor (e.g. "0.40.0",
// no "dN" dev-build suffix) -- see baseMinorVersion() below for how it's
// matched against the running version. Highlights are user-facing prose:
// no issue numbers, no internal slice/phase names, no jargon.
//
// This module is intentionally hand-curated rather than generated from
// commit history -- release notes are for users, not changelogs.

export interface ReleaseEntry {
  readonly version: string
  readonly date: string
  readonly title: string
  readonly highlights: readonly string[]
}

export const RELEASE_HISTORY: readonly ReleaseEntry[] = [
  {
    version: '0.42.0',
    date: '2026-07-03',
    title: 'Per-client configuration and protected catalog entries',
    highlights: [
      'Built-in catalog entries (payers, panels, procedure and diagnosis codes, providers, reference labs, and more) are now marked as system records and protected from accidental edits or deletion.',
      'Lab clients gained per-client result and requisition preferences: default result status, PDF file naming, barcode matching, and requisition layout options.',
      'Reference labs can now store an NPI number, shown wherever the lab is referenced.',
    ],
  },
  {
    version: '0.40.0',
    date: '2026-07-02',
    title: 'Support requests reach us automatically',
    highlights: [
      'Help & Support requests now forward straight to MedFlow support, with an email confirmation sent to you.',
      "If your deployment can't reach us online, the form falls back to a direct support email so nothing gets lost.",
    ],
  },
  {
    version: '0.39.0',
    date: '2026-07-02',
    title: 'Archived organizations stay visible',
    highlights: [
      'Archived organizations still show up by name (with an "Archived" badge) across the fleet and instrument views instead of disappearing.',
      'Lab data is archived rather than deleted, so historical records remain available.',
    ],
  },
  {
    version: '0.38.0',
    date: '2026-07-02',
    title: 'Archive and restore sites',
    highlights: [
      'Sites can now be archived and restored from the customer page.',
      'Archived site keys immediately stop authenticating, so retired instruments can no longer submit data.',
    ],
  },
  {
    version: '0.37.0',
    date: '2026-07-02',
    title: 'Automatic instrument results matching',
    highlights: [
      'Analyzer results now attach to the matching order automatically.',
      'Panels handle extra analytes and flag results for operator review when something needs a second look.',
    ],
  },
  {
    version: '0.36.0',
    date: '2026-07-02',
    title: 'California electronic lab reporting (CDPH)',
    highlights: ['Added support for submitting reportable results to CDPH SaPHIRE electronic lab reporting (ELR).'],
  },
  {
    version: '0.35.0',
    date: '2026-06-30',
    title: 'Label printing parity',
    highlights: [
      'A full label format catalog with a live preview before you print.',
      'Round-trip support for DYMO label printers.',
    ],
  },
]

// Extracts the "major.minor.patch" base from a running version string,
// ignoring any trailing dev-build suffix ("d0", "d3", ...) and git-sha
// metadata ("+a1b2c3d"). Runtime versions look like "0.40.0d0" or
// "0.40.0d0+a1b2c3d.dirty"; release-history entries are plain "0.40.0".
// Matching on this extracted base (rather than a raw String#startsWith
// against the entry version) avoids false-positive substring matches,
// e.g. a running "1.2.34d0" must not match a released "1.2.3".
export function baseMinorVersion(version: string): string {
  const match = /^(\d+\.\d+\.\d+)/.exec(version)
  return match ? match[1] : version
}
